import { Link } from 'react-router-dom'
import { ChevronLeft, Info } from 'lucide-react'
import { Alert, AlertDescription } from '@atrvd/saas-ui'
import { CONNECTION_COPY } from '@/components/ledgersync/connectionManagement.utils'
import {
  FACILITY_SCOPE_COPY,
  facilityFinancialAccountsLabel,
  financialAccountsBasePath,
  type FacilityFinancialAccountsScope,
} from './financialAccountsScope'

interface FinancialAccountsPageHeaderProps {
  /** The facility the page is scoped to; null on the organization-wide pages. */
  scope: FacilityFinancialAccountsScope | null
  /**
   * A detail page: show "Back to …" above the title, to the Financial Accounts
   * page of the same scope (the facility's or the organization-wide one).
   */
  backToList?: boolean
}

/**
 * Title block shared by the Financial Accounts settings pages: the
 * organization-wide title, or the facility's name with the facility subtitle
 * when scoped, plus the read-only notice for an inactive facility.
 */
export function FinancialAccountsPageHeader({ scope, backToList = false }: FinancialAccountsPageHeaderProps) {
  const listTitle = scope ? facilityFinancialAccountsLabel(scope.facilityName) : CONNECTION_COPY.pageTitle
  return (
    <div className="space-y-4">
      <div>
        {backToList && (
          <Link
            to={financialAccountsBasePath(scope)}
            className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft aria-hidden="true" className="mr-1 h-4 w-4" />
            {`Back to ${listTitle}`}
          </Link>
        )}
        <h1 className="text-3xl font-bold tracking-tight">
          {scope ? scope.facilityName : CONNECTION_COPY.pageTitle}
        </h1>
        <p className="text-muted-foreground">
          {scope ? FACILITY_SCOPE_COPY.subtitle : CONNECTION_COPY.pageSubtitle}
        </p>
      </div>
      {scope?.readOnly && (
        // `status`, not Alert's default `alert`: a standing notice on load, not
        // an interruption to announce assertively.
        <Alert variant="info" role="status">
          <Info aria-hidden="true" className="h-4 w-4" />
          <AlertDescription>{FACILITY_SCOPE_COPY.inactiveBanner}</AlertDescription>
        </Alert>
      )}
    </div>
  )
}
